import { useMemo, useState, type FormEvent } from "react";
import { PLAN_PERKS, type CouponScope, type SellerPlan } from "../../shared/market";
import { COUPON_MAX_PCT, type CouponKind, type SellerCoupon } from "../../shared/marketCoupons";
import { BOUGHT_SET, SEGMENTS } from "../../shared/marketCustomers";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { useArs } from "./components";
import { CouponShowcase } from "./Showcase";

/** Scopes this form offers (personal coupons come from the Clientes tab). */
const FORM_SCOPES: CouponScope[] = ["shop", "products", "set", "segment"];

/** "10% de descuento" / "$ 1.000 de descuento", with the minimum when there is one. */
export function useCouponOff() {
  const { t } = useI18n();
  const ars = useArs();
  return (c: { kind: CouponKind; value: number; minSubtotalArs: number | null }) =>
    t(c.kind === "percent" ? "cpn.off.percent" : "cpn.off.amount", { value: c.kind === "percent" ? c.value : ars(c.value) }) + (c.minSubtotalArs ? t("cpn.publicMin", { amount: ars(c.minSubtotalArs) }) : "");
}

/** Mi tienda › Cupones: the shop's coupons and a form for a new one, within the plan. */
export function CouponsTab({ plan, handle }: { plan: SellerPlan; /** For the example code a plan without coupons sees. */ handle: string }) {
  const { t } = useI18n();
  const perks = PLAN_PERKS[plan];
  const list = useAsync((signal) => (perks.coupons > 0 ? marketApi.coupons(signal) : Promise.resolve([] as SellerCoupon[])), [perks.coupons]);
  const [coupons, setCoupons] = useState<SellerCoupon[] | null>(null);
  const shown = coupons ?? list.data;
  if (perks.coupons === 0) return <CouponShowcase handle={handle} />;
  if (list.error) return <ErrorMessage onRetry={list.reload} />;
  if (!shown) return <Loading />;
  return (
    <div className="stack">
      <p className="muted small">{t("cpn.intro")}</p>
      <p className="small">{t("cpn.limit", { count: perks.coupons })}</p>
      <CouponList coupons={shown} onChange={setCoupons} />
      <CouponForm scopes={FORM_SCOPES.filter((s) => perks.couponScopes.includes(s))} onCreated={setCoupons} />
    </div>
  );
}

/** A group's name ("Recurrentes…", "Compraron de <set>"). */
function useSegmentLabel() {
  const { t } = useI18n();
  return (segment: string) => (segment.startsWith(BOUGHT_SET) ? t("cus.seg.boughtSet", { set: segment.slice(BOUGHT_SET.length) }) : t(`cus.seg.${segment}` as MessageKey));
}

function CouponList({ coupons, onChange }: { coupons: SellerCoupon[]; onChange: (c: SellerCoupon[]) => void }) {
  const { t, locale } = useI18n();
  const segmentLabel = useSegmentLabel();
  const ars = useArs();
  const off = useCouponOff();
  const [error, setError] = useState<string | null>(null);
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale === "es" ? "es-AR" : "en-US");
  const act = async (c: SellerCoupon, status: SellerCoupon["status"]) => {
    if (status === "ended" && !window.confirm(t("cpn.endConfirm", { code: c.code }))) return;
    setError(null);
    try {
      onChange(await marketApi.setCouponStatus(c.id, status));
    } catch (err) {
      setError(err instanceof ApiError && err.code === "coupon_limit" ? t("cpn.err.coupon_limit") : t("error.generic"));
    }
  };
  if (!coupons.length) return <p className="muted">{t("cpn.empty")}</p>;
  return (
    <>
      {error && <p className="form-error" role="alert">{error}</p>}
      <ul className="coupon-list">
        {coupons.map((c) => (
          <li key={c.id} className={c.status === "active" ? "coupon-row" : "coupon-row is-off"}>
            <div>
              <code className="coupon-code">{c.code}</code> <span className={c.status === "active" ? "pill pill-paid" : "pill"}>{t(`cpn.status.${c.status}` as MessageKey)}</span>
              {c.pausedReason === "plan" && <span className="muted small"> · {t("cpn.pausedPlan")}</span>}
              {c.pausedReason === "admin" && <span className="muted small"> · {t("cpn.pausedAdmin")}</span>}
              <div className="small">
                {off(c)}
                {c.maxDiscountArs ? ` · ${t("cpn.cap", { amount: ars(c.maxDiscountArs) })}` : ""} · {t(`cpn.scope.${c.scope}` as MessageKey)}
                {c.scope === "products" && `: ${c.products.map((p) => p.name).join(", ")}`}
                {c.scope === "set" && `: ${c.setName}`}
                {c.scope === "segment" && c.segment && `: ${segmentLabel(c.segment)}`}
                {c.scope === "user" && c.issuedTo && `: ${c.issuedTo}`}
              </div>
              <div className="muted small">
                {c.usesMax ? t("cpn.usesOf", { count: c.uses, max: c.usesMax }) : t("cpn.uses", { count: c.uses })} · {t("cpn.discounted", { amount: ars(c.discountedArs) })}
                {new Date(c.startsAt).getTime() > Date.now() && ` · ${t("cpn.from", { date: date(c.startsAt) })}`}
                {c.endsAt && ` · ${t("cpn.until", { date: date(c.endsAt) })}`}
              </div>
            </div>
            {c.status !== "ended" && (
              <div className="btn-row">
                {c.status === "active" ? (
                  <button type="button" className="btn btn-link btn-sm" onClick={() => void act(c, "paused")}>
                    {t("cpn.pause")}
                  </button>
                ) : c.pausedReason !== "admin" ? (
                  <button type="button" className="btn btn-link btn-sm" onClick={() => void act(c, "active")}>
                    {t("cpn.resume")}
                  </button>
                ) : null}
                <button type="button" className="btn btn-link btn-sm" onClick={() => void act(c, "ended")}>
                  {t("cpn.end")}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function CouponForm({ scopes, onCreated }: { scopes: CouponScope[]; onCreated: (c: SellerCoupon[]) => void }) {
  const { t } = useI18n();
  const [kind, setKind] = useState<CouponKind>("percent");
  const [scope, setScope] = useState<CouponScope>("shop");
  const [segment, setSegment] = useState<string>("repeat");
  const segmentLabel = useSegmentLabel();
  // How many people the chosen group has now (pokefans picks them; the shop sees the number).
  const size = useAsync((signal) => (scope === "segment" ? marketApi.segmentCount(segment, signal) : Promise.resolve(null)), [scope, segment]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  // Cards and sets the shop has listed, for coupons that cover only some.
  const listings = useAsync((signal) => (scopes.length > 1 ? marketApi.myListings(signal) : Promise.resolve([])), [scopes.length]);
  const products = useMemo(() => [...new Map((listings.data ?? []).map((l) => [l.productId, `${l.name}${l.setName ? ` · ${l.setName}` : ""}`])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [listings.data]);
  const sets = useMemo(() => [...new Set((listings.data ?? []).map((l) => l.setName).filter((n): n is string => Boolean(n)))].sort(), [listings.data]);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const text = (k: string) => String(f.get(k) ?? "").trim();
    const num = (k: string) => (text(k) === "" ? null : Number(text(k)));
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await marketApi.createCoupon({
          code: text("code"),
          kind,
          value: num("value"),
          minSubtotalArs: num("min"),
          maxDiscountArs: kind === "percent" ? num("cap") : null,
          scope,
          productIds: scope === "products" ? f.getAll("products").map(Number) : [],
          setName: scope === "set" ? text("set") : null,
          segment: scope === "segment" ? segment : null,
          usesMax: num("usesMax"),
          perUserMax: num("perUser") ?? 1,
          // The end of that day in Argentina.
          endsAt: text("endsAt") ? `${text("endsAt")}T23:59:59-03:00` : null,
        }),
      );
      setFormKey((n) => n + 1);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setError(code === "coupon_limit" || code === "coupon_code_taken" || code === "plan_required" ? t(`cpn.err.${code}` as MessageKey) : code === "validation_error" ? t("cpn.err.validation") : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form key={formKey} className="plan-card stack-sm coupon-form" onSubmit={submit} aria-labelledby="cpn-new">
      <h3 id="cpn-new">{t("cpn.new")}</h3>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="cpn-code">{t("cpn.code")}</label>
          <input id="cpn-code" name="code" required minLength={4} maxLength={16} pattern="[A-Za-z0-9]{4,16}" autoCapitalize="characters" />
          <span className="field-hint">{t("cpn.codeHint")}</span>
        </div>
        <div className="field">
          <label htmlFor="cpn-kind">{t("cpn.kind")}</label>
          <select id="cpn-kind" value={kind} onChange={(e) => setKind(e.target.value as CouponKind)}>
            <option value="percent">{t("cpn.percent")}</option>
            <option value="amount">{t("cpn.amount")}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="cpn-value">
            {t("cpn.value")} ({kind === "percent" ? "%" : "$"})
          </label>
          <input id="cpn-value" name="value" type="number" required min={1} max={kind === "percent" ? COUPON_MAX_PCT : undefined} />
        </div>
        <div className="field">
          <label htmlFor="cpn-min">{t("cpn.minSubtotal")}</label>
          <input id="cpn-min" name="min" type="number" min={1} />
        </div>
        {kind === "percent" && (
          <div className="field">
            <label htmlFor="cpn-cap">{t("cpn.maxDiscount")}</label>
            <input id="cpn-cap" name="cap" type="number" min={1} />
          </div>
        )}
        {scopes.length > 1 && (
          <div className="field">
            <label htmlFor="cpn-scope">{t("cpn.scope")}</label>
            <select id="cpn-scope" value={scope} onChange={(e) => setScope(e.target.value as CouponScope)}>
              {scopes.map((s) => (
                <option key={s} value={s}>
                  {t(`cpn.scope.${s}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
        )}
        {scope === "products" && (
          <div className="field">
            <label htmlFor="cpn-products">{t("cpn.products")}</label>
            <select id="cpn-products" name="products" multiple required size={Math.min(8, Math.max(3, products.length))}>
              {products.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}
        {scope === "segment" && (
          <div className="field">
            <label htmlFor="cpn-segment">{t("cus.segment")}</label>
            <select id="cpn-segment" value={segment} onChange={(e) => setSegment(e.target.value)}>
              {SEGMENTS.map((s) => (
                <option key={s} value={s}>
                  {segmentLabel(s)}
                </option>
              ))}
              {sets.map((n) => (
                <option key={n} value={`${BOUGHT_SET}${n}`}>
                  {segmentLabel(`${BOUGHT_SET}${n}`)}
                </option>
              ))}
            </select>
            {size.data && <span className="field-hint">{size.data.count === null ? t("cus.groupSizeFew") : t("cus.groupSize", { count: size.data.count })}</span>}
          </div>
        )}
        {scope === "set" && (
          <div className="field">
            <label htmlFor="cpn-set">{t("cpn.set")}</label>
            <select id="cpn-set" name="set" required>
              {sets.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="cpn-uses">{t("cpn.usesMax")}</label>
          <input id="cpn-uses" name="usesMax" type="number" min={1} />
        </div>
        <div className="field">
          <label htmlFor="cpn-per">{t("cpn.perUser")}</label>
          <input id="cpn-per" name="perUser" type="number" min={1} max={99} defaultValue={1} />
        </div>
        <div className="field">
          <label htmlFor="cpn-ends">{t("cpn.endsAt")}</label>
          <input id="cpn-ends" name="endsAt" type="date" />
        </div>
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {t("cpn.create")}
        </button>
      </div>
    </form>
  );
}
